// src/screens/Profile.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import BottomBar from "../components/BottomBar";
import { ReactComponent as Logo } from "../assets/images/logo.svg";
import { ReactComponent as Close } from "../assets/images/close.svg";
import { ReactComponent as Trash } from "../assets/images/trash.svg";
import Cookies from "js-cookie";
import { SESSION_NAME, useSessionContext } from "../contexts/session-context";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ReactComponent as Edit } from "../assets/images/Edit.svg";
import { ReactComponent as Cam } from "../assets/images/cam.svg";
import config from "../config/env";

const API_KEY = "darwintec-solutions-X-prompt-jai";
const PHOTO_DRAFT_KEY = "profile:draft_photo";

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
function dataURLToFile(dataUrl, filename = "photo.png", mime = "") {
  const arr = String(dataUrl).split(",");
  const m = arr[0]?.match(/:(.*?);/);
  const type = mime || (m ? m[1] : "image/png");
  const bstr = atob(arr[1] || "");
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], filename, { type });
}
function saveDraftPhotoToSession({ dataUrl, name, type }) {
  try {
    sessionStorage.setItem(PHOTO_DRAFT_KEY, JSON.stringify({ dataUrl, name, type }));
  } catch { }
}
function readDraftPhotoFromSession() {
  try {
    const raw = sessionStorage.getItem(PHOTO_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function clearDraftPhotoSession() {
  try {
    sessionStorage.removeItem(PHOTO_DRAFT_KEY);
  } catch { }
}

function uid(prefix = "id") {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}
function joinName(firstName, lastName) {
  return [String(firstName || "").trim(), String(lastName || "").trim()]
    .filter(Boolean)
    .join(" ");
}

function apiGenderToUi(g) {
  if (g === "male" || g === "ชาย") return "ชาย";
  if (g === "female" || g === "หญิง") return "หญิง";
  if (g === "unknown") return "ไม่ระบุ";
  return "ไม่ระบุ";
}
function uiGenderToApi(g) {
  if (g === "ชาย") return "male";
  if (g === "หญิง") return "female";
  if (g === "ไม่ระบุ") return "unknown";
  return null;
}

function buildHeaders(extra = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    "X-API-KEY": API_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function buildPhotoUrl(photoPath) {
  const base = String(config.api || "").replace(/\/$/, "");
  const p = String(photoPath || "").trim();
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;

  if (p.startsWith("/api/")) return `${base}${p}`;
  if (p.startsWith("/userImage/")) return `${base}/api/auth${p}`;

  const path = p.startsWith("/") ? p : `/${p}`;
  return `${base}${path}`;
}
async function fetchPhotoPreview(photoPath) {
  const url = buildPhotoUrl(photoPath);
  if (!url) return "";

  try {
    const res = await axios.get(url, {
      headers: buildHeaders(),
      responseType: "blob",
      withCredentials: false,
    });
    return URL.createObjectURL(res.data);
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 403) throw e;
    return "";
  }
}


function UserCircleIcon() {
  return (
    <div className="h-[90px] w-[90px] rounded-full bg-[#3E6F5E] grid place-items-center">
      <svg width="46" height="46" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5Z"
          fill="white"
          opacity="0.95"
        />
        <path
          d="M4 22c0-4.418 3.582-8 8-8h0c4.418 0 8 3.582 8 8"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[14px] font-medium text-[#4B5563]">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SuccessModal({
  open,
  title = "บันทึกข้อมูลสำเร็จ",
  desc = "ระบบบันทึกการแก้ไขข้อมูลเรียบร้อยแล้ว",
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-[1px] grid place-items-center px-4"
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="w-full max-w-[520px] rounded-2xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M20 6 9 17l-5-5"
              stroke="#059669"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="mt-4 text-center text-[18px] font-semibold text-slate-900">
          {title}
        </div>
        <div className="mt-2 text-center text-[14px] text-slate-600">{desc}</div>
      </div>
    </div>
  );
}

function ConfirmLogoutModal({ open, loading, onClose, onConfirm }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-[1px] grid place-items-center px-4"
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[520px] rounded-2xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="text-center text-[18px] font-semibold text-slate-900">
          ออกจากระบบใช่หรือไม่
        </div>

        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-11 w-[140px] rounded-xl border border-red-300 bg-white text-red-500 font-medium disabled:opacity-60"
          >
            ไม่ใช่
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="h-11 w-[140px] rounded-xl bg-[#D64040] text-white font-medium disabled:opacity-60"
          >
            {loading ? "กำลังออก..." : "ใช่"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { session, setSession } = useSessionContext();
  const nav = useNavigate();

  const [bootLoading, setBootLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [userId, setUserId] = useState(
    session?.user?.id || Number(localStorage.getItem("USER_ID")) || null
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [genderUi, setGenderUi] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  const [provinceId, setProvinceId] = useState("");
  const [hubId, setHubId] = useState(""); // (จริงๆ คือ amphure/hub id)
  const [hubText, setHubText] = useState("");
  const [hubNameFromApi, setHubNameFromApi] = useState(""); // ถ้า backend ส่งชื่อมา

  const [provinces, setProvinces] = useState([{ id: "", name_th: "เลือกจังหวัด" }]);
  const [amphures, setAmphures] = useState([{ id: "", name_th: "เลือกอำเภอ/เขต" }]);

  const [plates, setPlates] = useState([]);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const fileInputRef = useRef(null);
  const originalRef = useRef(null);

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);

  const fullName = useMemo(
    () => joinName(firstName, lastName) || "ชื่อ - นามสกุล",
    [firstName, lastName]
  );

  function revokeIfBlob(url) {
    try {
      if (url && String(url).startsWith("blob:")) URL.revokeObjectURL(url);
    } catch { }
  }

  function formatPhoneDigits(v) {
    return String(v || "").replace(/[^\d]/g, "").slice(0, 10);
  }

  function findNameById(list, id) {
    const key = String(id ?? "");
    if (!key) return "";
    const row = Array.isArray(list) ? list.find((x) => String(x.id) === key) : null;
    return row?.name_th || row?.name_en || row?.name || "";
  }

  function buildHubTextFromAddress({ province_id, hub_id, hub_name }) {
    const provinceName =
      findNameById(provinces, province_id) ||
      (province_id ? `จังหวัด ${province_id}` : "จังหวัด");

    const amphureName =
      hub_name ||
      findNameById(amphures, hub_id) ||
      (hub_id ? `อำเภอ/เขต ${hub_id}` : "อำเภอ/เขต");

    return `${amphureName} , ${provinceName}`;
  }

  const provinceName = useMemo(() => {
    return findNameById(provinces, provinceId) || "";
  }, [provinces, provinceId]);

  const amphureName = useMemo(() => {
    return hubNameFromApi || findNameById(amphures, hubId) || "";
  }, [amphures, hubId, hubNameFromApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${config.api || ""}/api/location/provinces`, {
          headers: buildHeaders(),
          withCredentials: false,
        });
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        if (!cancelled) setProvinces([{ id: "", name_th: "เลือกจังหวัด" }, ...list]);
      } catch (e) {
        console.error("load provinces error", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!provinceId) {
        setAmphures([{ id: "", name_th: "เลือกอำเภอ/เขต" }]);
        return;
      }
      try {
        const res = await axios.get(
          `${config.api || ""}/api/location/amphures/${provinceId}`,
          { headers: buildHeaders(), withCredentials: false }
        );
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        if (!cancelled) setAmphures([{ id: "", name_th: "เลือกอำเภอ/เขต" }, ...list]);
      } catch (e) {
        console.error("load amphures error", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [provinceId]);

  useEffect(() => {
    setHubText(
      buildHubTextFromAddress({
        province_id: provinceId || "",
        hub_id: hubId || "",
        hub_name: hubNameFromApi || "",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceId, hubId, provinces, amphures, hubNameFromApi]);

  async function hydrateProfile(targetId = userId) {
    const res = await axios.get(`${config.api || ""}/api/auth/profile/${targetId}`, {
      headers: buildHeaders(),
      withCredentials: false,
    });

    const u = res?.data?.user || null;
    if (!u?.id) throw new Error("no user");

    setUserId(u.id);
    localStorage.setItem("USER_ID", String(u.id));

    setFirstName(u.first_name ?? "");
    setLastName(u.last_name ?? "");
    setPhone(u.phone_number ?? "");
    setGenderUi(apiGenderToUi(u.gender));
    setUsername(u.username ?? "");

    const vs = Array.isArray(u.vehicles) ? u.vehicles : [];
    const nextPlates = vs.length
      ? vs.map((v, idx) => ({
        id: uid("plate"),
        label: `ทะเบียนรถคันที่ ${idx + 1}`,
        value: String(v?.license_plate || "").trim(),
      }))
      : [{ id: uid("plate"), label: "ทะเบียนรถคันที่ 1", value: "" }];

    setPlates(nextPlates);

    const addr0 = Array.isArray(u.addresses) ? u.addresses[0] : null;
    const pId = addr0?.province_id ? String(addr0.province_id) : "";
    const hId = addr0?.hub_id ? String(addr0.hub_id) : String(addr0?.amphure_id || "");

    setProvinceId(pId);
    setHubId(hId);
    setHubNameFromApi(addr0?.hub_name || "");

    const preview = await fetchPhotoPreview(u.photo || "");
    setPhotoPreview((prev) => {
      revokeIfBlob(prev);
      return preview || "";
    });

    originalRef.current = {
      firstName: u.first_name ?? "",
      lastName: u.last_name ?? "",
      genderUi: apiGenderToUi(u.gender),
      phone: u.phone_number ?? "",
      username: u.username ?? "",
      provinceId: pId,
      hubId: hId,
      hubNameFromApi: addr0?.hub_name || "",
      plates: nextPlates,
      photoPreview: preview || "",
    };

    return u;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setBootLoading(true);
        setErr("");
        setOk("");
        await hydrateProfile(userId);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          nav("/login", { replace: true });
          return;
        }
        console.error(e);
        if (!cancelled) setErr("โหลดข้อมูลไม่สำเร็จ");
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => revokeIfBlob(photoPreview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addPlate() {
    setPlates((prev) => [
      ...prev,
      { id: uid("plate"), label: `ทะเบียนรถคันที่ ${prev.length + 1}`, value: "" },
    ]);
  }
  function updatePlate(id, value) {
    setPlates((prev) =>
      prev
        .map((x) => (x.id === id ? { ...x, value } : x))
        .map((x, idx) => ({ ...x, label: `ทะเบียนรถคันที่ ${idx + 1}` }))
    );
  }
  function removePlate(id) {
    setPlates((prev) => {
      const next = prev.filter((x) => x.id !== id);
      const safe = next.length
        ? next
        : [{ id: uid("plate"), label: "ทะเบียนรถคันที่ 1", value: "" }];
      return safe.map((x, idx) => ({ ...x, label: `ทะเบียนรถคันที่ ${idx + 1}` }));
    });
  }

  // ---------- restore photo draft when editing ----------
  useEffect(() => {
    if (!isEditing) return;
    if (photoFile) return;

    const draft = readDraftPhotoFromSession();
    if (!draft?.dataUrl) return;

    setPhotoPreview(draft.dataUrl);
    try {
      const f = dataURLToFile(draft.dataUrl, draft.name || "photo.png", draft.type || "");
      setPhotoFile(f);
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  async function onPickPhoto(file) {
    if (!file) return;

    setPhotoFile(file);
    setPhotoPreview((prev) => {
      revokeIfBlob(prev);
      return URL.createObjectURL(file);
    });

    try {
      const dataUrl = await fileToDataURL(file);
      saveDraftPhotoToSession({ dataUrl, name: file.name, type: file.type });
    } catch { }
  }

  function removePhoto() {
    setPhotoFile("");
    setPhotoPreview((prev) => {
      revokeIfBlob(prev);
      return "";
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    clearDraftPhotoSession();
  }

  async function startEdit() {
    setErr("");
    setOk("");
    setPassword("");
    setPassword2("");

    try {
      setBootLoading(true);
      await hydrateProfile(userId);
    } catch (e) {
      console.error("refresh before edit error", e);
    } finally {
      setBootLoading(false);
    }
    setIsEditing(true);
  }

  function cancelEdit() {
    const o = originalRef.current;
    if (o) {
      setFirstName(o.firstName || "");
      setLastName(o.lastName || "");
      setGenderUi(o.genderUi || "");
      setPhone(o.phone || "");
      setUsername(o.username || "");
      setProvinceId(o.provinceId || "");
      setHubId(o.hubId || "");
      setHubNameFromApi(o.hubNameFromApi || "");
      setPlates(
        Array.isArray(o.plates)
          ? o.plates
          : [{ id: uid("plate"), label: "ทะเบียนรถคันที่ 1", value: "" }]
      );

      setPassword("");
      setPassword2("");

      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    clearDraftPhotoSession();
    setIsEditing(false);
  }

  const saveProfile = async () => {
    if (saving) return;

    try {
      setSaving(true);
      setErr("");
      setOk("");

      if (!userId) throw new Error("missing userId");

      const genderApi = uiGenderToApi(genderUi);
      if (!genderApi) {
        setErr("กรุณาเลือกเพศ");
        return;
      }

      const phoneDigits = formatPhoneDigits(phone);
      if (phoneDigits.length !== 10) {
        setErr("กรุณากรอกเบอร์โทรให้ถูกต้อง (10 หลัก)");
        return;
      }

      const cleanPlates = plates.map((p) => String(p?.value || "").trim()).filter(Boolean);
      if (!cleanPlates.length) {
        setErr("กรุณากรอกทะเบียนรถอย่างน้อย 1 คัน");
        return;
      }

      if (password || password2) {
        if (!password || !password2) {
          setErr("กรุณากรอกรหัสผ่านให้ครบ 2 ช่อง");
          return;
        }
        if (password !== password2) {
          setErr("รหัสผ่านไม่ตรงกัน");
          return;
        }
      }

      const fd = new FormData();
      fd.append("first_name", String(firstName || "").trim());
      fd.append("last_name", String(lastName || "").trim());
      fd.append("phone_number", phoneDigits);
      if (String(username || "").trim()) fd.append("username", String(username).trim());
      if (password) fd.append("password", String(password));
      fd.append("gender", genderApi);

      fd.append(
        "address",
        JSON.stringify({
          hub_id: Number(hubId || 0),
          province_id: Number(provinceId || 0),
        })
      );

      fd.append("vehicles", JSON.stringify(cleanPlates.map((p) => ({ license_plate: p }))));

      if (photoFile) fd.append("photo", photoFile);

      await axios.put(`${config.api || ""}/api/auth/edit-profile/${userId}`, fd, {
        headers: buildHeaders(),
        withCredentials: false,
      });

      await hydrateProfile(userId);

      clearDraftPhotoSession();
      setPhotoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      setOk("บันทึกข้อมูลสำเร็จ");
      setIsEditing(false);
      setSaveSuccessOpen(true);
      setTimeout(() => setSaveSuccessOpen(false), 2000);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "บันทึกข้อมูลไม่สำเร็จ");
      alert("เกิดข้อผิดพลาด");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      try { localStorage.clear(); } catch { }
      try { sessionStorage.clear(); } catch { }
      try { Cookies.remove(SESSION_NAME, { path: "/" }); } catch { }
      try { Cookies.remove("token", { path: "/" }); } catch { }
      setSession({ isAuthenticated: false, user: null, loading: false, token: "" });
      nav("/login", { replace: true });
    } catch {
      nav("/login", { replace: true });
    } finally {
      setLogoutLoading(false);
      setLogoutOpen(false);
    }
  };


  return (
    <div className="min-h-screen bg-white pb-28">
      <header className="border-b">
        <div className="mx-auto max-w-sm px-4 py-3">
          <div className="flex items-center justify-between">
            <Logo className="h-14 w-[86px] text-emerald-700" aria-hidden />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-sm px-4">
        <div className="mt-5 flex items-center justify-between">
          <div className="text-[20px] font-medium text-[#3E6F5E]">
            {isEditing ? "แก้ไขข้อมูลส่วนตัว" : "ข้อมูลส่วนตัว"}
          </div>

          {!isEditing ? (
            <button
              type="button"
              onClick={startEdit}
              disabled={bootLoading}
              className="flex items-center gap-2 text-[14px] text-[#4B5563]"
            >
              <span>แก้ไขข้อมูลส่วนตัว</span>
              <Edit />
            </button>
          ) : (
            <div />
          )}
        </div>

        {bootLoading && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-[14px] text-slate-500">
            กำลังโหลดข้อมูล…
          </div>
        )}

        {/* ---------- VIEW MODE ---------- */}
        {!bootLoading && !isEditing && (
          <>
            <div className="mt-6 grid place-items-center">
              {photoPreview ? (
                <div className="h-[90px] w-[90px] rounded-full overflow-hidden border border-slate-200">
                  <img
                    src={photoPreview}
                    alt="profile"
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <UserCircleIcon />
              )}
            </div>

            <div className="mt-4">
              <div className="text-[16px] font-medium text-slate-900 text-center">
                {fullName}
              </div>
              <div className="mt-4 text-[14px] text-[#111827]">
                เพศ {genderUi ? `${genderUi}, ` : ""}
                {phone || "0812345678"}
              </div>

              {/* ✅ แสดงชื่ออำเภอ + ชื่อจังหวัด */}
              <div className="mt-1 text-[14px] text-[#4B5563]">
                {hubText || "อำเภอ/เขต , จังหวัด"}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[14px] font-medium text-slate-900">ทะเบียนรถที่เข้ารับ</div>
              <div className="mt-2 overflow-hidden rounded-md border border-[#F3F0DC] bg-white">
                {plates.map((p, idx) => (
                  <div
                    key={p.id}
                    className={[
                      "flex items-center justify-between px-4 py-3 text-[14px]",
                      idx === 0 ? "" : "border-t border-[#F3F0DC]",
                    ].join(" ")}
                  >
                    <div className="text-[#111827]">{p.label}</div>
                    <div className="font-medium text-[#111827]">{p.value || "—"}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="my-6 h-[1px] bg-[#E5E7EB]" />

            <button
              type="button"
              onClick={() => setLogoutOpen(true)}
              className="w-full h-12 rounded-xl border border-red-300 bg-red-50 text-red-500 font-medium"
            >
              ออกจากระบบ
            </button>
          </>
        )}

        <SuccessModal open={saveSuccessOpen} onClose={() => setSaveSuccessOpen(false)} />
        <ConfirmLogoutModal
          open={logoutOpen}
          loading={logoutLoading}
          onClose={() => !logoutLoading && setLogoutOpen(false)}
          onConfirm={handleLogout}
        />

        {/* ---------- EDIT MODE ---------- */}
        {!bootLoading && isEditing && (
          <>
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="ชื่อ" required>
                  <input
                    className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-3 text-[14px] outline-none"
                    placeholder="กรอกชื่อ"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Field>
                <Field label="นามสกุล" required>
                  <input
                    className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-3 text-[14px] outline-none"
                    placeholder="กรอกนามสกุล"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Field>
              </div>

              <Field label="เพศ" required>
                <div className="relative">
                  <select
                    className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-3 text-[14px] outline-none appearance-none pr-9"
                    value={genderUi}
                    onChange={(e) => setGenderUi(e.target.value)}
                  >
                    <option value="">เลือกเพศ</option>
                    <option value="ชาย">ชาย</option>
                    <option value="หญิง">หญิง</option>
                    <option value="ไม่ระบุ">ไม่ระบุ</option>
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                    ▾
                  </span>
                </div>
              </Field>

              <Field label="หมายเลขโทรศัพท์" required>
                <input
                  className="w-full h-11 rounded-xl border bg-gray-200 px-3 text-[14px] outline-none"
                  placeholder="0xxxxxxxxx"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneDigits(e.target.value))}
                  disabled
                />
              </Field>

              <Field label="username" required={false}>
                <input
                  className="w-full h-11 rounded-xl border bg-gray-200 px-3 text-[14px] outline-none"
                  placeholder="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled
                />
              </Field>
              {/* 
              <div className="grid grid-cols-2 gap-3">
                <Field label="ตั้งรหัสผ่านใหม่ (ถ้าต้องการ)" required={false}>
                  <input
                    className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-3 text-[14px] outline-none"
                    placeholder="รหัสผ่านใหม่"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
                <Field label="ยืนยันรหัสผ่านใหม่" required={false}>
                  <input
                    className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-3 text-[14px] outline-none"
                    placeholder="ยืนยันรหัสผ่านใหม่"
                    type="password"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                  />
                </Field>
              </div> */}

              {/* ✅ แสดงชื่อจังหวัด/อำเภอ (read-only) */}
              <Field label="จังหวัด" required>
                <input
                  className="w-full h-11 rounded-xl border bg-gray-200 px-3 text-[14px] outline-none"
                  value={provinceName || "—"}
                  disabled
                />
              </Field>

              <Field label="อำเภอ/เขต" required>
                <input
                  className="w-full h-11 rounded-xl border bg-gray-200 px-3 text-[14px] outline-none"
                  value={amphureName || "—"}
                  disabled
                />
              </Field>

              <div className="flex items-center justify-end gap-2">
                <div className="text-[13px] text-[#4B5563]">กดเพิ่มถ้ามีรถมากกว่า1คัน</div>
                <button
                  type="button"
                  onClick={addPlate}
                  className="h-9 rounded-lg bg-[#ED9C50] px-5 text-[14px] font-medium text-white"
                >
                  เพิ่ม
                </button>
              </div>

              <Field label="ทะเบียนรถที่เข้ารับ" required>
                <div className="space-y-3">
                  {plates.map((p) => (
                    <div key={p.id} className="flex items-center gap-3">
                      <input
                        className="flex-1 h-11 rounded-xl border border-[#9CA3AF] bg-white px-3 text-[14px] outline-none"
                        placeholder="กรอกทะเบียนรถที่เข้ารับ"
                        value={p.value}
                        onChange={(e) => updatePlate(p.id, e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removePlate(p.id)}
                        className="h-11 w-11 rounded-xl bg-[#F7E3E3] grid place-items-center"
                        aria-label="ลบทะเบียนรถ"
                      >
                        <Trash />
                      </button>
                    </div>
                  ))}
                </div>
              </Field>

              {/* ✅ upload photo */}
              <Field label="แนบรูปถ่าย (สำหรับติดบัตร)" required>
                <div className="rounded-2xl border border-[#9CA3AF] bg-white p-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickPhoto(e.target.files?.[0] || null)}
                  />

                  <div className="flex items-center justify-center">
                    <div className="relative w-[140px] h-[160px] rounded-xl overflow-hidden grid place-items-center bg-slate-100">
                      {photoPreview ? (
                        <>
                          <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={removePhoto}
                            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 grid place-items-center"
                            aria-label="ลบรูป"
                          >
                            <Close className="w-3.5 h-3.5 text-slate-600" />
                          </button>
                        </>
                      ) : (
                        <div className="text-center text-[#4B5563]">
                          <Cam />
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-10 text-[#4B5563] font-medium"
                  >
                    อัปโหลดรูป
                  </button>

                  {photoFile && (
                    <div className="mt-2 text-[12px] text-slate-500 text-center">
                      เลือกไฟล์: {photoFile.name}
                    </div>
                  )}
                </div>
              </Field>

              {!!err && <div className="mt-1 text-[12px] text-red-500 text-right">{err}</div>}
              {!!ok && <div className="mt-1 text-[12px] text-emerald-700 text-right">{ok}</div>}
            </div>

            <div className="h-24" />
            <div className="fixed bottom-[80px] left-0 right-0 bg-white">
              <div className="mx-auto px-4 py-4 border-t border-[#9CA3AF] rounded-t-3xl">
                <div className="flex gap-3 flex-row justify-between">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="w-1/4 h-11 rounded-xl border border-[#9CA3AF] bg-white text-[#4B5563] font-medium"
                    disabled={saving}
                  >
                    กลับ
                  </button>
                  <button
                    type="button"
                    onClick={saveProfile}
                    className="w-1/4 h-11 rounded-xl bg-[#3E6F5E] text-white font-medium disabled:opacity-60"
                    disabled={saving}
                  >
                    {saving ? "กำลังบันทึก…" : "ยืนยัน"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <div className="mt-2 text-center text-[12px] text-[#A1A1AA]">
        version 1.0.0
      </div>

      <BottomBar />
    </div>
  );
}
